import {
  type Preferences,
  type PreferencesEnum
} from '@shared/all/domains/user';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUserState } from '../context/UserContext';
import toast from '../helpers/toast';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

const PREFERENCES_KEY = ['users', 'preferences'] as const;

const defaults: Preferences = {
  awardsVisibilityPrivate: false,
  allowProfanity: false,
  betaAccess: false,
  allowPoliticalContent: false,
  hidePostsOnProfile: false,
  hideProfileHistory: false,
  allowNSFWContent: false,
  privatePinnedFactiiis: false,
  publicBeta: false,
  factiiiCountPrivate: false
};

const usePreference = (client: WrappedTRPCClient) => {
  const queryClient = useQueryClient();
  const { state } = useUserState();

  const { data } = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => {
      const [, prefs] = await client.users.preferences.query();
      return prefs ?? defaults;
    },
    enabled: state.user.isAuthenticated === true,
    staleTime: Infinity
  });
  const preferenceState = data ?? defaults;

  const updatePreferences = async (preference: PreferencesEnum) => {
    const previous = preferenceState;
    const next = { ...previous, [preference]: !previous[preference] };

    queryClient.setQueryData(PREFERENCES_KEY, next);

    const [error, result] = await client.users.updatePreferences.mutate({
      payload: next
    });
    if (error || !result?.updated) {
      queryClient.setQueryData(PREFERENCES_KEY, previous);
      toast(
        'Something went wrong',
        'Preferences could not be updated at the moment. Please try again later.',
        'error'
      );
    }
  };

  const setPinnedFactiiisVisibility = async () => {
    const [error, toggle] =
      await client.users.togglePinnedFactiiisVisibility.mutate();
    if (error) {
      toast(
        'Unable to change pins visibility',
        error.message || 'An error occurred',
        'error'
      );
      return;
    }
    if (toggle) {
      queryClient.setQueryData(PREFERENCES_KEY, {
        ...preferenceState,
        privatePinnedFactiiis: toggle.isPrivate
      });
    }
  };

  return {
    preferenceState,
    updatePreferences,
    setPinnedFactiiisVisibility
  };
};

export default usePreference;
